import { Fragment, useRef, useState } from 'react';
import {
  Filter,
  HandleGroupSelectAllArgs,
  HandleMassSelectArgs,
  HandleSelectedArgs,
  PageData,
  Region,
} from './types';
import { useIsScrolled } from '../../utils/hooks';
import { Dialog, Transition } from '@headlessui/react';
import clsx from 'clsx';
import Accordion from '../accordion';
import Checkbox from './checkbox';
import Button from '../button';
import { RegionMenuMobile } from './region-menu';

type FilterMenuProps = Readonly<{
  isOpen: boolean;
  showSearch: boolean;
  onClose: () => void;
  filters: Filter[];
  handleSelected: HandleSelectedArgs;
  handleGroupSelectAll: HandleGroupSelectAllArgs;
  handleMassSelect: HandleMassSelectArgs;
  handleReset: () => void;
  handleSearch: (e: React.FormEvent) => void;
  applyFilters: () => void;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  strings: PageData['strings'];
  regions: Region[];
  regionFilterName: string;
}>;

export default function FilterMenu({
  isOpen,
  showSearch,
  onClose,
  filters,
  handleSelected,
  handleGroupSelectAll,
  handleMassSelect,
  handleReset,
  handleSearch,
  applyFilters,
  searchQuery,
  setSearchQuery,
  strings,
  regions,
  regionFilterName,
}: FilterMenuProps) {
  const [openAccordion, setOpenAccordion] = useState('');
  const panelRef = useRef(null);
  const [isScrolled] = useIsScrolled(panelRef);

  return (
    <Transition appear as={Fragment} show={isOpen} enterTo="filter-menu--open">
      <Dialog onClose={onClose} className="filter-menu">
        <Dialog.Panel
          className={clsx('filter-menu__panel', { scrolled: isScrolled })}
          ref={panelRef}
        >
          {showSearch ? (
            <div className="filter-menu__search">
              <form className="search-form" onSubmit={handleSearch}>
                <input
                  className="search-form__input"
                  type="text"
                  placeholder={strings.searchPlaceHolder}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
                <button
                  onClick={handleSearch}
                  type="button"
                  title={strings.searchCta}
                >
                  <i className="icon-search search-form__icon" />
                </button>
                <button type="submit" className="button-primary">
                  {strings.searchCta}
                </button>
              </form>
            </div>
          ) : null}

          <button
            type="button"
            className="filter-menu__reset"
            onClick={handleReset}
          >
            {strings.resetAll}
          </button>

          <Dialog.Title className="filter-menu__title">
            {strings.filter}
          </Dialog.Title>

          <div className="filter-menu__filters">
            {filters.map(filter => {
              const selectedIndex = filter.facets.filter(
                facet => facet.selected
              ).length;

              return regionFilterName === filter.filterProp ? (
                <RegionMenuMobile
                  {...{
                    filter,
                    selectedIndex,
                    openAccordion,
                    setOpenAccordion,
                    handleSelected,
                    handleMassSelect,
                    regions,
                  }}
                  scrollableParent={panelRef}
                  key={`accordion-${filter.name}`}
                />
              ) : (
                <Accordion
                  title={`${filter.name} ${
                    selectedIndex ? '(' + selectedIndex + ')' : ''
                  }`}
                  key={`accordion-${filter.name}`}
                  onClick={setOpenAccordion}
                  close={filter.name !== openAccordion}
                  scrollableParent={panelRef}
                >
                  <ul className="filter-menu__list">
                    {filter.facets.length >= 2 ? (
                      <li>
                        <Checkbox.SelectAll
                          filter={filter}
                          callback={() =>
                            handleGroupSelectAll(
                              filter.name,
                              !filter.facets.every(facet => facet.selected)
                            )
                          }
                        />
                      </li>
                    ) : null}
                    {filter.facets.map(facet => (
                      <li key={`checkbox-item-${facet.id}`}>
                        <Checkbox
                          facet={facet}
                          callback={() => handleSelected(facet.id)}
                        />
                      </li>
                    ))}
                  </ul>
                </Accordion>
              );
            })}
          </div>

          <div className="filter-menu__buttons">
            <Button
              className="filter-menu__button filter-menu__button--close"
              content={strings.close}
              childLeft={<i className="icon-cross" />}
              onClick={onClose}
            />
            <Button
              className="filter-menu__button filter-menu__button--apply"
              content={strings.applyFilters}
              childRight={<i className="icon-arrow-right" />}
              onClick={applyFilters}
            />
          </div>
        </Dialog.Panel>
      </Dialog>
    </Transition>
  );
}
